// spec: specs/bulk-orders.md
class BulkOrders extends HTMLElement {
    constructor() {
        super();

        this.inputs = Array.from(this.querySelectorAll('[data-variant-id]'));
        this.totalUnitsEl = this.querySelector('[data-bulk-orders-total-units]');
        this.totalPriceEl = this.querySelector('[data-bulk-orders-total-price]');
        this.liveRegion = this.querySelector('[data-bulk-orders-live]');
        this.errorEl = this.querySelector('[data-bulk-orders-error]');
        this.submitBtn = this.querySelector('[data-bulk-orders-submit]');
        this.spinner = this.querySelector('.bulk-orders__submit-spinner');

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex, 10);
        this._filterMissingOverride = this.dataset.stringFilterMissingOverride || null;

        this.boundOnInput = this.onInput.bind(this);
        this.inputs.forEach((input) => {
            input.addEventListener('input', this.boundOnInput);
            input.addEventListener('change', this.boundOnInput);
            input.addEventListener('paste', () => setTimeout(() => this.onInput({ target: input }), 0));
        });

        if (this.submitBtn) {
            this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
        }

        this._setupScrollIndicators();
        this._setupFilters();
    }

    _setupScrollIndicators() {
        this.scrollContainer = this.querySelector('.bulk-orders__scroll');
        this.scrollWrap = this.querySelector('.bulk-orders__scroll-wrap');
        if (!this.scrollContainer || !this.scrollWrap) return;

        this.boundUpdateScrollIndicators = this.updateScrollIndicators.bind(this);
        this.scrollContainer.addEventListener('scroll', this.boundUpdateScrollIndicators, { passive: true });

        if (typeof ResizeObserver === 'function') {
            this.scrollObserver = new ResizeObserver(this.boundUpdateScrollIndicators);
            this.scrollObserver.observe(this.scrollContainer);
        }

        this.updateScrollIndicators();
    }

    updateScrollIndicators() {
        if (!this.scrollContainer || !this.scrollWrap) return;
        const { scrollLeft, clientWidth, scrollWidth } = this.scrollContainer;
        this.scrollWrap.classList.toggle('is-scrollable-start', scrollLeft > 0);
        this.scrollWrap.classList.toggle('is-scrollable-end', scrollLeft + clientWidth < scrollWidth - 1);
    }

    _setupFilters() {
        this.filterFieldsets = Array.from(this.querySelectorAll('[data-bulk-orders-filter]'));
        this.helperEl = this.querySelector('[data-bulk-orders-filters-helper]');
        if (this.filterFieldsets.length === 0) return;

        this.columnCells = Array.from(
            this.querySelectorAll('[data-variant-column-index]')
        );

        this.boundOnFilterChange = this._onFilterChange.bind(this);
        this.filterFieldsets.forEach((fieldset) => {
            fieldset.addEventListener('change', this.boundOnFilterChange);
        });

        this._applyFilters();
    }

    _onFilterChange() {
        this._applyFilters();
    }

    _collectActiveFilters() {
        const active = {};
        this.filterFieldsets.forEach((fieldset) => {
            const idx = parseInt(fieldset.dataset.optionIndex, 10);
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            if (checked) active[idx] = checked.value;
        });
        return active;
    }

    _firstMissingFilter() {
        for (let i = 0; i < this.filterFieldsets.length; i++) {
            const fs = this.filterFieldsets[i];
            if (!fs.querySelector('input[type="radio"]:checked')) {
                return fs.dataset.optionName || '';
            }
        }
        return null;
    }

    _applyFilters() {
        const active = this._collectActiveFilters();
        const totalFilters = this.filterFieldsets.length;
        const allSelected = Object.keys(active).length === totalFilters;

        const visibleColumns = new Set();
        const columnsByIndex = new Map();
        this.columnCells.forEach((cell) => {
            const colIdx = cell.dataset.variantColumnIndex;
            if (!columnsByIndex.has(colIdx)) columnsByIndex.set(colIdx, []);
            columnsByIndex.get(colIdx).push(cell);
        });

        columnsByIndex.forEach((cells, colIdx) => {
            let matches = true;
            Object.keys(active).forEach((idx) => {
                const expected = active[idx];
                // fix: specs/fixes/bulk-orders-data-option-attr-mismatch.md — attribute is data-optionN (no hyphen before digit) so dataset key is optionN. HTML spec only triggers camelCase for hyphen followed by lowercase letters, not digits.
                const cellValue = cells[0].dataset['option' + (parseInt(idx, 10) + 1)];
                if (cellValue !== expected) matches = false;
            });
            const hidden = !matches;
            cells.forEach((cell) => cell.classList.toggle('bulk-orders--hidden', hidden));
            if (!hidden) visibleColumns.add(colIdx);
        });

        this.inputs.forEach((input) => {
            const colIdx = input.closest('[data-variant-column-index]').dataset.variantColumnIndex;
            const isVisible = visibleColumns.has(colIdx);
            const isAvailable = input.dataset.variantAvailable === 'true';

            // fix: specs/fixes/bulk-orders-preserve-qty-across-filters.md — do NOT reset hidden inputs to 0; preserve their value so the user can accumulate quantities across multiple filter combos (e.g. multiple colors) in a single cart-add. Hidden inputs stay `disabled` so the user can't edit them while their column is filtered out, but their value persists and contributes to totals + submit payload.
            input.disabled = !allSelected || !isVisible || !isAvailable;
        });

        this.classList.toggle('bulk-orders--filters-pending', !allSelected);

        this.filterFieldsets.forEach((fs) => {
            const selectedSpan = fs.querySelector('[data-bulk-orders-filter-selected]');
            const checked = fs.querySelector('input[type="radio"]:checked');
            if (selectedSpan) selectedSpan.textContent = checked ? checked.value : '';
        });

        if (this.submitBtn) {
            if (allSelected) {
                this.submitBtn.removeAttribute('disabled');
                this.submitBtn.removeAttribute('aria-disabled');
            } else {
                this.submitBtn.setAttribute('disabled', 'true');
                this.submitBtn.setAttribute('aria-disabled', 'true');
            }
        }

        if (this.helperEl) {
            if (allSelected) {
                this.helperEl.textContent = '';
            } else if (this._filterMissingOverride) {
                this.helperEl.textContent = this._filterMissingOverride;
            } else {
                const missing = this._firstMissingFilter();
                if (missing) {
                    this.helperEl.textContent = this.t('filter_missing', { option: missing });
                } else {
                    this.helperEl.textContent = this.t('filters_pending');
                }
            }
        }

        this.recalcTotals();
    }

    disconnectedCallback() {
        if (this.scrollContainer && this.boundUpdateScrollIndicators) {
            this.scrollContainer.removeEventListener('scroll', this.boundUpdateScrollIndicators);
        }
        if (this.scrollObserver) {
            this.scrollObserver.disconnect();
            this.scrollObserver = null;
        }
        if (this.filterFieldsets && this.boundOnFilterChange) {
            this.filterFieldsets.forEach((fs) => {
                fs.removeEventListener('change', this.boundOnFilterChange);
            });
        }
    }

    onInput(evt) {
        const input = evt.target;
        if (input.disabled) return;

        let value = parseInt(input.value, 10);
        if (Number.isNaN(value) || value < 0) value = 0;

        const max = input.dataset.max ? parseInt(input.dataset.max, 10) : null;
        if (max !== null && value > max) {
            value = max;
            this.announce(this.t('stock_capped', { max }));
        }

        if (String(value) !== input.value) input.value = value;

        this.recalcTotals();
    }

    recalcTotals() {
        let totalUnits = 0;
        let totalCents = 0;
        // fix: specs/fixes/bulk-orders-preserve-qty-across-filters.md — count ALL inputs (visible + hidden by filter) so the totals reflect the cumulative pending cart across all filter combos. Pairs with the value-preservation in _applyFilters above.
        this.inputs.forEach((input) => {
            const qty = parseInt(input.value, 10) || 0;
            const priceCents = parseInt(input.dataset.variantPrice, 10) || 0;
            totalUnits += qty;
            totalCents += qty * priceCents;
        });

        if (this.totalUnitsEl) this.totalUnitsEl.textContent = String(totalUnits);
        if (this.totalPriceEl) {
            this.totalPriceEl.innerHTML = (window.Shopify && Shopify.formatMoney)
                ? Shopify.formatMoney(totalCents, this.moneyFormat)
                : this.moneyFormat.replace('{{amount}}', (totalCents / 100).toFixed(2));
        }
    }

    onSubmit(evt) {
        evt.preventDefault();
        this.clearError();

        // AC-32: short-circuit if filters are pending (defensive — button should already be disabled).
        if (this.classList.contains('bulk-orders--filters-pending')) {
            if (this._filterMissingOverride) {
                this.showError(this._filterMissingOverride);
            } else {
                const missing = this._firstMissingFilter();
                this.showError(missing ? this.t('filter_missing', { option: missing }) : this.t('filters_pending'));
            }
            return;
        }

        const items = this.inputs
            .map((input) => ({ id: parseInt(input.dataset.variantId, 10), quantity: parseInt(input.value, 10) || 0 }))
            .filter((item) => item.quantity > 0);

        if (items.length === 0) {
            this.showError(this.t('no_items'));
            return;
        }

        this.setLoading(true);

        // fix: specs/fixes/bulk-orders-undefined-redirect.md — use Halo theme cart pattern (Shopify.getCart + halo.updateSidebarCart) instead of cart-notification; fallback redirects to window.routes.cart (not cart_url, which doesn't exist on this theme).
        fetch(window.routes.cart_add_url, { ...fetchConfig('javascript'), body: JSON.stringify({ items }) })
            .then((response) => {
                if (!response.ok) return response.json().then((err) => { throw err; });
                return response.json();
            })
            .then(() => {
                const haloReady = window.Shopify && typeof window.Shopify.getCart === 'function'
                    && window.halo && typeof window.halo.updateSidebarCart === 'function';
                if (haloReady) {
                    window.Shopify.getCart((cart) => {
                        window.halo.updateSidebarCart(cart);
                        document.body.classList.add('cart-sidebar-show');
                    });
                    this.resetInputs();
                    this.recalcTotals();
                } else {
                    window.location.href = window.routes.cart;
                }
            })
            .catch((err) => {
                const msg = (err && err.description) ? err.description : this.t('network_error');
                this.showError(msg);
            })
            .finally(() => {
                this.setLoading(false);
            });
    }

    setLoading(isLoading) {
        if (!this.submitBtn) return;
        if (isLoading) {
            this.submitBtn.setAttribute('disabled', 'true');
            this.submitBtn.classList.add('loading');
            if (this.spinner) this.spinner.classList.remove('hidden');
        } else {
            this.submitBtn.removeAttribute('disabled');
            this.submitBtn.classList.remove('loading');
            if (this.spinner) this.spinner.classList.add('hidden');
            // Re-apply filter gating after loading clears.
            if (this.filterFieldsets && this.filterFieldsets.length > 0) this._applyFilters();
        }
    }

    resetInputs() {
        // fix: specs/fixes/bulk-orders-preserve-qty-across-filters.md — reset all inputs unconditionally after a successful cart-add. Previously we skipped disabled inputs (safe under v1.4–v1.6 because hidden inputs were force-reset to 0 on every filter change). Now hidden inputs preserve their qty across filter changes and may carry qty>0 into the submit payload; after a successful cart-add we must clear them too. Out-of-stock variants (also disabled) were always at 0, so resetting them is a no-op.
        this.inputs.forEach((input) => { input.value = 0; });
    }

    showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.add('bulk-orders__error--visible');
    }

    clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.classList.remove('bulk-orders__error--visible');
    }

    announce(message) {
        if (!this.liveRegion) return;
        this.liveRegion.textContent = '';
        // Re-set after a tick so screen readers pick up the change reliably.
        setTimeout(() => { this.liveRegion.textContent = message; }, 50);
    }

    t(key, vars) {
        const dataKey = 'string' + key.split('_').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');
        let str = this.dataset[dataKey] || key;
        if (vars) {
            Object.keys(vars).forEach((k) => {
                str = str.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), vars[k])
                         .replace(new RegExp('__' + k.toUpperCase() + '__', 'g'), vars[k]);
            });
        }
        return str;
    }
}

customElements.define('bulk-orders', BulkOrders);
